import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Truck, CheckCircle2, Loader2, Car, Camera, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";

const CATEGORY_OPTIONS = [
  "taxi", "moving", "towing", "bus",
  "crane", "luxury", "mechanic", "tire",
  "delivery", "carwash",
];

const TRUCK_CATS = new Set(["moving", "towing"]);

export default function SupplierFleetCard() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [specs, setSpecs] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/me/vehicles");
      setVehicles(Array.isArray(data) ? data : []);
    } catch (_) {
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/vehicle-specs").then((r) => setSpecs(r.data)).catch(() => {});
  }, []);

  const setActive = async (v) => {
    try {
      const { data } = await api.post(`/me/vehicles/${v.vehicle_id}/activate`);
      // Refresh cached auth user so downstream UI (Nearby map, dashboard cards)
      // pick up the new dispatch-active vehicle specs.
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch (_) { /* non-fatal */ }
      toast.success(t("fleet.activated", `${v.label} is now your active vehicle`));
      load();
    } catch (e) {
      const msg = formatApiError(e) || t("fleet.activateFailed", "Could not set active vehicle — please retry");
      toast.error(msg);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(t("fleet.confirmDelete", `Remove ${v.label}?`))) return;
    try {
      await api.delete(`/me/vehicles/${v.vehicle_id}`);
      toast.success(t("fleet.removed", "Vehicle removed"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mb-4 bg-ink-surface border border-ink-border rounded-2xl p-4" data-testid="supplier-fleet-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase font-black text-ink-textDim tracking-widest">
            {t("fleet.title", "My Fleet")}
          </div>
          <div className="text-sm font-black text-white truncate mt-0.5">
            {vehicles.length === 0
              ? t("fleet.empty", "No vehicles yet — add one to receive dispatches")
              : t("fleet.count", { count: vehicles.length, defaultValue: `${vehicles.length} vehicles registered` })}
          </div>
        </div>
        <AddVehicleDialog specs={specs} onDone={load} />
      </div>

      {loading ? (
        <div className="p-6 flex items-center justify-center text-ink-textDim">
          <Loader2 size={16} className="animate-spin" />
        </div>
      ) : vehicles.length === 0 ? null : (
        <div className="space-y-2" data-testid="fleet-list">
          {vehicles.map((v) => (
            <FleetRow key={v.vehicle_id} v={v} onActivate={() => setActive(v)} onDelete={() => remove(v)} />
          ))}
        </div>
      )}
    </div>
  );
}

function FleetRow({ v, onActivate, onDelete }) {
  const { t } = useTranslation();
  const chips = [
    v.taxi_type && t(`taxi.${v.taxi_type}`, v.taxi_type),
    v.bus_type && t(`bus.${v.bus_type}`, v.bus_type),
    v.truck_tonnage_band && t(`truck.${v.truck_tonnage_band}`, v.truck_tonnage_band),
    v.crane_tonnage_band && t(`crane.${v.crane_tonnage_band}`, v.crane_tonnage_band),
    v.seating_capacity && `${v.seating_capacity} ${t("taxi.seatsShort", "seats")}`,
  ].filter(Boolean);
  const Icon = v.category === "taxi" || v.category === "luxury" || v.category === "limousine" ? Car : Truck;

  return (
    <div
      className={`rounded-xl p-3 border flex items-center gap-3 flex-wrap ${
        v.active ? "border-brand-accent bg-brand-accent/10" : "border-ink-border bg-ink-surface2"
      }`}
      data-testid={`fleet-row-${v.vehicle_id}`}
    >
      {(() => {
        const cover = v.photo || (v.photos && v.photos[0]);
        return cover ? (
          <img
            src={cover}
            alt={v.label}
            className="w-12 h-12 rounded-lg object-cover border border-ink-border"
            data-testid={`fleet-photo-${v.vehicle_id}`}
          />
        ) : (
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${v.active ? "bg-brand-accent text-black" : "bg-ink-border text-white"}`}>
            <Icon size={18} />
          </div>
        );
      })()}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-black text-white truncate" data-testid={`fleet-label-${v.vehicle_id}`}>{v.label}</div>
          {v.active && (
            <span className="text-[10px] font-black uppercase bg-brand-accent text-black rounded-full px-2 py-0.5 tracking-widest inline-flex items-center gap-1" data-testid={`fleet-active-${v.vehicle_id}`}>
              <CheckCircle2 size={10} /> {t("fleet.active", "Active")}
            </span>
          )}
        </div>
        <div className="text-[11px] text-ink-textDim mt-0.5">
          <span className="uppercase font-bold">{t(`categories.${v.category}`, v.category)}</span>
          {v.plate && <span> · {v.plate}</span>}
          {v.model && <span> · {v.model}</span>}
        </div>
        {chips.length > 0 && (
          <div className="mt-1 flex gap-1 flex-wrap">
            {chips.map((c, i) => (
              <span key={i} className="text-[10px] font-bold text-white/80 bg-ink-border rounded-full px-2 py-0.5">{c}</span>
            ))}
          </div>
        )}
      </div>
      {!v.active && (
        <button
          onClick={onActivate}
          className="text-[11px] font-black bg-white text-black rounded-full px-3 py-1.5"
          data-testid={`fleet-activate-${v.vehicle_id}`}
        >
          {t("fleet.setActive", "Set active")}
        </button>
      )}
      <button
        onClick={onDelete}
        className="p-2 rounded-full hover:bg-red-500/20 text-red-400"
        data-testid={`fleet-delete-${v.vehicle_id}`}
        aria-label="delete"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function toPatch(v) {
  const p = {
    label: v.label,
    category: v.category,
    plate: v.plate || null,
    color: v.color || null,
    model: v.model || null,
    taxi_type: v.taxi_type || null,
    bus_type: v.bus_type || null,
    seating_capacity: v.seating_capacity || null,
    truck_tonnage_band: v.truck_tonnage_band || null,
    crane_tonnage_band: v.crane_tonnage_band || null,
    luxury_with_driver: v.luxury_with_driver ?? null,
    luxury_without_driver: v.luxury_without_driver ?? null,
  };
  return p;
}

function AddVehicleDialog({ specs, onDone }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(initialForm());

  const reset = () => setForm(initialForm());

  const submit = async () => {
    if (!form.label.trim()) return toast.error(t("fleet.needLabel", "Give this vehicle a name"));
    if (!form.category) return toast.error(t("fleet.needCategory", "Pick a service category"));
    setBusy(true);
    try {
      const payload = {
        label: form.label.trim(),
        category: form.category,
        plate: form.plate || null,
        color: form.color || null,
        model: form.model || null,
        photo: form.photo || null,
        photos: (form.photos && form.photos.length) ? form.photos : null,
        active: false,
      };
      if (form.category === "taxi") {
        payload.taxi_type = form.taxi_type || null;
        payload.seating_capacity = form.seating_capacity ? parseInt(form.seating_capacity, 10) : null;
      }
      if (form.category === "bus") {
        payload.bus_type = form.bus_type || null;
        payload.seating_capacity = form.seating_capacity ? parseInt(form.seating_capacity, 10) : null;
      }
      if (TRUCK_CATS.has(form.category)) {
        payload.truck_tonnage_band = form.truck_tonnage_band || null;
      }
      if (form.category === "crane") {
        payload.crane_tonnage_band = form.crane_tonnage_band || null;
      }
      if (form.category === "luxury") {
        // Chauffeur-only (self-drive removed by product decision).
        payload.luxury_with_driver = true;
        payload.luxury_style = form.luxury_style || null;
        payload.luxury_hourly_rate = form.luxury_hourly_rate ? Number(form.luxury_hourly_rate) : null;
        payload.luxury_daily_rate = form.luxury_daily_rate ? Number(form.luxury_daily_rate) : null;
      }
      await api.post("/me/vehicles", payload);
      toast.success(t("fleet.added", "Vehicle added"));
      setOpen(false);
      reset();
      onDone && onDone();
    } catch (e) {
      const msg = formatApiError(e) || t("fleet.addFailed", "Could not add vehicle — please retry");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const isTaxi = form.category === "taxi";
  const isBus = form.category === "bus";
  const isTruck = TRUCK_CATS.has(form.category);
  const isCrane = form.category === "crane";
  const isLuxury = form.category === "luxury";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button
          className="bg-white text-black rounded-full px-3 py-1.5 text-[11px] font-black inline-flex items-center gap-1.5"
          data-testid="fleet-add-btn"
        >
          <Plus size={12} /> {t("fleet.addVehicle", "Add vehicle")}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-ink-surface border-ink-border max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">{t("fleet.addVehicle", "Add vehicle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label={t("fleet.name", "Nickname")}>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={t("fleet.namePh", "e.g. Blue Camry")}
              className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
              data-testid="fleet-input-label"
            />
          </Field>
          <Field label={t("fleet.category", "Service category")}>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
              data-testid="fleet-input-category"
            >
              <option value="">—</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{t(`categories.${c}`, c)}</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("fleet.plate", "Plate")}>
              <input
                value={form.plate}
                onChange={(e) => setForm({ ...form, plate: e.target.value })}
                className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
                data-testid="fleet-input-plate"
              />
            </Field>
            <Field label={t("fleet.color", "Color")}>
              <input
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
                data-testid="fleet-input-color"
              />
            </Field>
          </div>
          <Field label={t("fleet.model", "Model")}>
            <input
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              placeholder={t("fleet.modelPh", "e.g. Toyota Camry 2022")}
              className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
              data-testid="fleet-input-model"
            />
          </Field>

          <PhotoGalleryField
            primary={form.photo}
            gallery={form.photos || []}
            onPrimaryChange={(dataUrl) => setForm({ ...form, photo: dataUrl })}
            onGalleryChange={(list) => setForm({ ...form, photos: list })}
          />

          {isTaxi && (
            <Field label={t("auth.taxiClass", "Taxi class")}>
              <div className="grid grid-cols-2 gap-2">
                {(specs?.taxi?.types || []).map((tt) => (
                  <button
                    key={tt.id}
                    type="button"
                    onClick={() => setForm({ ...form, taxi_type: tt.id, seating_capacity: form.seating_capacity || String(tt.seats) })}
                    className={`rounded-xl p-2 border-2 text-start ${form.taxi_type === tt.id ? "border-white bg-white/10" : "border-ink-border"}`}
                    data-testid={`fleet-taxi-${tt.id}`}
                  >
                    <div className="text-[11px] font-black text-white">{t(`taxi.${tt.id}`, tt.id)}</div>
                    <div className="text-[10px] text-ink-textDim">×{tt.multiplier.toFixed(1)}</div>
                  </button>
                ))}
              </div>
            </Field>
          )}

          {isBus && (
            <Field label={t("auth.busTier", "Bus tier")}>
              <div className="grid grid-cols-3 gap-2">
                {(specs?.bus?.types || []).map((bt) => (
                  <button
                    key={bt.id}
                    type="button"
                    onClick={() => setForm({ ...form, bus_type: bt.id, seating_capacity: form.seating_capacity || String(bt.seats) })}
                    className={`rounded-xl p-2 border-2 text-start ${form.bus_type === bt.id ? "border-white bg-white/10" : "border-ink-border"}`}
                    data-testid={`fleet-bus-${bt.id}`}
                  >
                    <div className="text-[11px] font-black text-white">{t(`bus.${bt.id}`, bt.id)}</div>
                    <div className="text-[10px] text-ink-textDim">×{bt.multiplier.toFixed(1)}</div>
                  </button>
                ))}
              </div>
            </Field>
          )}

          {(isTaxi || isBus) && (
            <Field label={t("auth.seatingCapacity", "Seating capacity")}>
              <input
                type="number" min="1" max="60"
                value={form.seating_capacity}
                onChange={(e) => setForm({ ...form, seating_capacity: e.target.value })}
                className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
                data-testid="fleet-input-seats"
              />
            </Field>
          )}

          {isTruck && (
            <Field label={t("auth.truckTonnage", "Truck capacity")}>
              <div className="grid grid-cols-2 gap-2">
                {(specs?.truck?.bands || []).map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setForm({ ...form, truck_tonnage_band: b.id })}
                    className={`rounded-xl p-2 border-2 text-start ${form.truck_tonnage_band === b.id ? "border-white bg-white/10" : "border-ink-border"}`}
                    data-testid={`fleet-truck-${b.id}`}
                  >
                    <div className="text-[11px] font-black text-white">{t(`truck.${b.id}`, b.id)}</div>
                  </button>
                ))}
              </div>
            </Field>
          )}

          {isCrane && (
            <Field label={t("auth.craneTonnage", "Crane tonnage")}>
              <div className="grid grid-cols-2 gap-2">
                {(specs?.crane?.bands || []).map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setForm({ ...form, crane_tonnage_band: b.id })}
                    className={`rounded-xl p-2 border-2 text-start ${form.crane_tonnage_band === b.id ? "border-white bg-white/10" : "border-ink-border"}`}
                    data-testid={`fleet-crane-${b.id}`}
                  >
                    <div className="text-[11px] font-black text-white">{t(`crane.${b.id}`, b.id)}</div>
                  </button>
                ))}
              </div>
            </Field>
          )}

          {isLuxury && (
            <>
              <Field label={t("fleet.luxuryStyle", "Body style (shown to customers)")}>
                <div className="flex flex-wrap gap-2">
                  {["sedan", "suv", "limousine", "supercar", "convertible"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({ ...form, luxury_style: s })}
                      className={`rounded-full px-3 py-1.5 text-[11px] font-black border-2 ${form.luxury_style === s ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-ink-border"}`}
                      data-testid={`fleet-luxury-style-${s}`}
                    >
                      {t(`luxury.style_${s}`, s.charAt(0).toUpperCase() + s.slice(1))}
                    </button>
                  ))}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t("fleet.hourlyRate", "Hourly rate (leave blank if not offered)")}>
                  <input
                    type="number" min="0" step="1"
                    value={form.luxury_hourly_rate}
                    onChange={(e) => setForm({ ...form, luxury_hourly_rate: e.target.value })}
                    placeholder="e.g. 150"
                    className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
                    data-testid="fleet-luxury-hourly"
                  />
                </Field>
                <Field label={t("fleet.dailyRate", "Daily rate")}>
                  <input
                    type="number" min="0" step="1"
                    value={form.luxury_daily_rate}
                    onChange={(e) => setForm({ ...form, luxury_daily_rate: e.target.value })}
                    placeholder="e.g. 900"
                    className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-3 py-2 text-sm outline-none focus:border-white"
                    data-testid="fleet-luxury-daily"
                  />
                </Field>
              </div>
              <div className="text-[10px] text-ink-textDim -mt-1">
                {t("fleet.hourlyDailyHint", "All luxury rentals are chauffeur-driven. Rates are per your local currency.")}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={submit}
            disabled={busy}
            className="w-full bg-brand-accent text-black rounded-full py-2.5 text-xs font-black disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="fleet-submit-btn"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {t("fleet.save", "Add to fleet")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">{label}</div>
      {children}
    </label>
  );
}

function PhotoGalleryField({ primary, gallery, onPrimaryChange, onGalleryChange }) {
  const { t } = useTranslation();
  const MAX_GALLERY = 6;

  const readFile = (file) =>
    new Promise((resolve, reject) => {
      if (file.size > 500 * 1024) {
        reject(new Error("too_large"));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const onPrimaryPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await readFile(file);
      onPrimaryChange(dataUrl);
    } catch (_) {
      toast.error(t("fleet.photoTooLarge", "Photo too large — pick one under 500KB"));
    }
    e.target.value = "";
  };

  const onGalleryPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_GALLERY - gallery.length;
    if (room <= 0) {
      toast.error(t("fleet.galleryFull", "Gallery limit is 6 photos"));
      e.target.value = "";
      return;
    }
    const picks = files.slice(0, room);
    try {
      const dataUrls = await Promise.all(picks.map(readFile));
      onGalleryChange([...gallery, ...dataUrls]);
    } catch (_) {
      toast.error(t("fleet.photoTooLarge", "Photo too large — pick one under 500KB"));
    }
    e.target.value = "";
  };

  const removeGallery = (i) => {
    const copy = [...gallery];
    copy.splice(i, 1);
    onGalleryChange(copy);
  };

  const promoteToPrimary = (i) => {
    const chosen = gallery[i];
    const copy = [...gallery];
    copy.splice(i, 1);
    if (primary) copy.push(primary);
    onGalleryChange(copy);
    onPrimaryChange(chosen);
  };

  return (
    <div className="block">
      <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">
        {t("fleet.photoGallery", "Vehicle photos (up to 7)")}
      </div>

      {/* Primary photo slot */}
      {primary ? (
        <div className="flex items-center gap-3 mb-2">
          <img src={primary} alt="primary" className="w-16 h-16 rounded-xl object-cover border border-amber-400" data-testid="fleet-photo-preview" />
          <div className="flex-1">
            <div className="text-[11px] text-amber-200 font-black uppercase">{t("fleet.cover", "Cover photo")}</div>
            <div className="text-[10px] text-ink-textDim">{t("fleet.photoHint", "Customers see this exact car pulling up")}</div>
            <button
              type="button"
              onClick={() => onPrimaryChange("")}
              className="mt-1 text-[11px] font-bold text-red-400 inline-flex items-center gap-1"
              data-testid="fleet-photo-remove"
            >
              <X size={12} /> {t("fleet.removePhoto", "Remove photo")}
            </button>
          </div>
        </div>
      ) : (
        <label
          htmlFor="fleet-photo-input"
          className="w-full cursor-pointer bg-ink-surface2 border border-dashed border-ink-border rounded-xl px-3 py-3 text-xs font-black text-white inline-flex items-center justify-center gap-2 hover:border-white mb-2"
          data-testid="fleet-photo-picker"
        >
          <Camera size={14} /> {t("fleet.cover", "Cover photo")}
        </label>
      )}
      <input id="fleet-photo-input" type="file" accept="image/*" onChange={onPrimaryPick} className="hidden" data-testid="fleet-photo-input" />

      {/* Gallery grid */}
      <div className="grid grid-cols-4 gap-2 mt-1">
        {gallery.map((src, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-ink-border group" data-testid={`fleet-gallery-item-${i}`}>
            <img src={src} className="w-full h-full object-cover" alt={`angle ${i + 1}`} />
            <button
              type="button"
              onClick={() => removeGallery(i)}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"
              data-testid={`fleet-gallery-remove-${i}`}
            >
              <X size={10} />
            </button>
            <button
              type="button"
              onClick={() => promoteToPrimary(i)}
              className="absolute bottom-1 left-1 right-1 text-[9px] font-black uppercase bg-white/80 text-black rounded-md py-0.5"
              data-testid={`fleet-gallery-cover-${i}`}
            >
              {t("fleet.setCover", "Set as cover")}
            </button>
          </div>
        ))}
        {gallery.length < MAX_GALLERY && (
          <label
            htmlFor="fleet-gallery-input"
            className="aspect-square rounded-lg border border-dashed border-ink-border flex flex-col items-center justify-center cursor-pointer hover:border-white text-ink-textDim"
            data-testid="fleet-gallery-add"
          >
            <Camera size={14} />
            <span className="text-[9px] font-black uppercase mt-1">+{MAX_GALLERY - gallery.length}</span>
          </label>
        )}
      </div>
      <input id="fleet-gallery-input" type="file" accept="image/*" multiple onChange={onGalleryPick} className="hidden" data-testid="fleet-gallery-input" />
      <div className="text-[10px] text-ink-textDim mt-2">
        {t("fleet.galleryHint", "Interior, exterior, wheels — customers can pre-vet the exact car.")}
      </div>
    </div>
  );
}

function initialForm() {
  return {
    label: "",
    category: "",
    plate: "",
    color: "",
    model: "",
    taxi_type: "",
    bus_type: "",
    seating_capacity: "",
    truck_tonnage_band: "",
    crane_tonnage_band: "",
    luxury_style: "",
    luxury_hourly_rate: "",
    luxury_daily_rate: "",
    photo: "",
    photos: [],
  };
}
